import {
  LeadImportTemplateColumnModel,
  LeadImportTemplateModel
} from '../../shared/models/lead.model';

/**
 * CSV in and out for the lead upload.
 *
 * Deliberately not a spreadsheet library. The admin ships no CSV or XLSX
 * parser today and adding one for a flat, single-sheet template would be a
 * megabyte of dependency to read a file with no formulas, no merged cells and
 * no second tab. Excel opens and saves CSV, which is the whole journey: the
 * team downloads the template, fills it in Excel, saves as CSV, uploads it.
 *
 * It does mean Excel still mangles phone numbers on the way out — a column of
 * digits saved as a number loses its leading zero. That damage is undone
 * server-side at the import boundary and bad rows are reported individually,
 * so the fix lives where the file is read rather than being something the
 * person filling in the sheet has to know. The template's phone column also
 * carries the "format as Text" warning.
 */

/**
 * Byte-order mark.
 *
 * Written as an escape rather than the literal character: a BOM pasted into
 * source is invisible, and the next person to touch this line would have no
 * way to see why it is there or that deleting it broke Arabic names in Excel.
 */
const BOM = '\uFEFF';

/** Values that need quoting in a CSV field. */
const NEEDS_QUOTING = /[",\n\r]/;

function escapeCell(value: unknown): string {
  const text: string = value === null || value === undefined ? '' : String(value);
  return NEEDS_QUOTING.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

/**
 * The template as a downloadable CSV: a header row and one example row.
 *
 * The example row is included rather than described. A format shown in the
 * file is one nobody has to be told, and "2026-11-03" in the cell settles the
 * date-format question that otherwise comes back as a support message.
 */
export function templateToCsv(template: LeadImportTemplateModel): string {
  const header: string = template.columns
    .map((column: LeadImportTemplateColumnModel) =>
      escapeCell(column.required ? `${column.label} *` : column.label)
    )
    .join(',');

  return `${header}\n${template.example.map(escapeCell).join(',')}\n`;
}

/**
 * Splits one CSV line, respecting quotes.
 *
 * Hand-rolled because the alternative is a dependency, and the grammar that
 * matters here is small: quoted fields, doubled quotes inside them, commas
 * everywhere else. A naive `split(',')` breaks on the first address or the
 * first comment containing a comma, which both appear in real sheets.
 */
function splitCsvLine(line: string): string[] {
  const cells: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i += 1) {
    const char: string = line[i];

    if (inQuotes) {
      if (char === '"') {
        // A doubled quote inside a quoted field is one literal quote.
        if (line[i + 1] === '"') {
          current += '"';
          i += 1;
        } else {
          inQuotes = false;
        }
      } else {
        current += char;
      }
      continue;
    }

    if (char === '"') inQuotes = true;
    else if (char === ',') {
      cells.push(current);
      current = '';
    } else current += char;
  }

  cells.push(current);
  return cells.map((cell: string) => cell.trim());
}

export interface ParsedCsv {
  headers: string[];
  rows: string[][];
}

/** A CSV file as headers and rows, blank lines dropped. */
export function parseCsv(text: string): ParsedCsv {
  const lines: string[] = text
    // Excel on Windows writes CRLF; a stray \r becomes part of the last cell.
    .replace(/\r\n?/g, '\n')
    .split('\n')
    .filter((line: string) => line.trim().length > 0);

  if (!lines.length) return { headers: [], rows: [] };

  return {
    headers: splitCsvLine(lines[0]),
    rows: lines.slice(1).map(splitCsvLine)
  };
}

/**
 * Maps parsed rows onto the keys the import endpoint expects.
 *
 * Matched on the template's labels, case- and asterisk-insensitive, because
 * the header a person uploads is the header we gave them — possibly with the
 * required marker still on it, possibly re-cased by Excel.
 *
 * A column the template does not know is **ignored rather than passed
 * through**. The service rejects a key the vertical does not declare, so one
 * stray column somebody added for their own notes would otherwise fail every
 * row in the file.
 */
export function rowsToImportPayload(
  parsed: ParsedCsv,
  template: LeadImportTemplateModel
): { rows: Record<string, unknown>[]; unmatchedHeaders: string[] } {
  const byLabel = new Map<string, string>();
  template.columns.forEach((column: LeadImportTemplateColumnModel) =>
    byLabel.set(normaliseHeader(column.label), column.key)
  );

  const keyForIndex: (string | undefined)[] = parsed.headers.map((header: string) =>
    byLabel.get(normaliseHeader(header))
  );

  const unmatchedHeaders: string[] = parsed.headers.filter(
    (_: string, index: number) => !keyForIndex[index]
  );

  const rows: Record<string, unknown>[] = parsed.rows.map((cells: string[]) => {
    const row: Record<string, unknown> = {};
    keyForIndex.forEach((key: string | undefined, index: number) => {
      if (!key) return;
      const value: string = cells[index] ?? '';
      if (value !== '') row[key] = value;
    });
    return row;
  });

  return { rows, unmatchedHeaders };
}

function normaliseHeader(header: string): string {
  return header.replace(/\*/g, '').trim().toLowerCase();
}

/**
 * Hands the browser a file.
 *
 * A data: URL rather than a Blob URL — the admin runs behind a CSP that has
 * blocked blob downloads before, and a CSV small enough to be a template is
 * small enough to inline.
 */
export function downloadCsv(filename: string, csv: string): void {
  const link: HTMLAnchorElement = document.createElement('a');
  // The BOM is what makes Excel read UTF-8 rather than mangling Arabic names.
  link.href = `data:text/csv;charset=utf-8,${encodeURIComponent(`${BOM}${csv}`)}`;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}
